import { Injectable } from '@angular/core';

import { AuthManagerService } from '@services/auth/auth-manager.service';
/**
 * This Guard decides if a component like "screen", "action" or "field" can be `activated` or `not`.
 *
 * This class should not be modified directly, you can provide your own service which should extend the default one as follows:
 ```
  export class MyPolicyService extends PolicyService {
   ...
  }
 ```
 * And provide your class in the `core/custom-core.module.ts` file as follows:
 ```
  providers: [
   { provide: PolicyService, useClass: MyPolicyService }
  ]
 ```
 * */
@Injectable({
  providedIn: 'root'
})
export class PolicyService {

  constructor(private authService: AuthManagerService) { }
  /**
   * Decides if the provided policies' roles are `restricted` or `not`
   * @param policies
   * @returns {boolean}
   */
  isRestricted(policies: Policy[]): boolean {
    if (policies) {
      const roles = this.authService.getRoles();
      return roles.every(
        role => policies.find(policy => policy.role === role) && true
      );
    }
    return false;
  }

  /**
   * Decides if the screen's roles are `authorized` or `not`
   * @param screenPolicies
   * @returns {boolean}
   */
  isScreenAuthorized(screenPolicies: Policy[]): boolean {
    return !this.isRestricted(screenPolicies);
  }

  /**
   * Decides if the action's access is `restricted` or `not`
   * @param actionPolicies
   * @param action
   * @returns {boolean}
   */
  isActionRestricted(actionPolicies: Policy[], action?: string): boolean {
    return this.isRestricted(actionPolicies);
  }
  /**
   * Decides if the field's access is `restricted` or `not`
   * @param fieldPolicies
   * @param field
   * @returns {boolean}
   */
  isFieldRestricted(fieldPolicies: Policy[], field?: string): boolean {
    return this.isRestricted(fieldPolicies);
  }
}
/**
 * Policy contract interface
 */
export interface Policy {
  /**
   * The role property
   */
  role: string;
  /**
   * The condition property
   */
  condition?: {
    expression: string;
    calculationBase: string[];
  };
}
